import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";

import { Calendar, MapPin, Clock, Video, Mic, Loader2 } from "lucide-react";

import StatusBadge from "components/generic/StatusBadge";

import { platformNames } from "data/staticData";
import { createOrGetStream } from "services/livestreamService";


const LiveStreamCard = ({ event }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const { project, stream } = event;

  if (!project) return null;

  const handleManageClick = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await createOrGetStream(project.stream_project_booking_id);
      if (response.success) {
        // Redirect to manage page - could potentially pass stream data in state if needed
        navigate(`/livestream/${project.stream_project_booking_id}`, { state: { project, stream: response.data } });
      } else {
        alert(response.message || "Failed to initialize stream. Please try again.");
      }
    } catch (error) {
      console.error("Management Error:", error);
      alert("An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const parseJsonSafe = (data, fallback = []) => {
    if (!data) return fallback;
    if (typeof data === 'object') return data;
    try {
      return JSON.parse(data);
    } catch (e) {
      return fallback;
    }
  };

  const getLocation = () => {
    if (!project.event_location) return "N/A";
    try {
      const loc = JSON.parse(project.event_location);
      return loc.address || project.event_location;
    } catch (e) {
      return project.event_location;
    }
  };

  const getStatus = () => {
    if (project.is_completed) return "Completed";
    if (project.is_cancelled) return "Cancelled";
    if (project.is_active) return "Active";
    if (project.is_draft) return "Draft";
    return "Pending";
  };

  const platforms = parseJsonSafe(project.streaming_platforms);
  const equipment = parseJsonSafe(project.equipments_needed);

  return (
    <div key={project.stream_project_booking_id} className="bg-card border shadow-sm rounded-xl h-full flex flex-col">
      <div className="p-6 flex-grow">
        <div className="flex justify-between items-center mb-4">
          <StatusBadge status={getStatus()} />
          <div className="flex -space-x-2 max-w-64 overflow-hidden md:max-w-full pl-2 md:pl-0">
            {/* Crew members not directly in project object, placeholder or remove if not available */}
            {/* {event.crewMembers?.map((crew, index) => (
              <img key={index} src="https://i.pravatar.cc/30"
                className="w-8 h-8 rounded-full border-2 border-white" />
            ))} */}
          </div>
        </div>

        <h3 className="text-xl font-semibold mb-2 line-clamp-1">{project.project_name}</h3>

        <div className="flex flex-wrap gap-3 text-sm mb-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Calendar size={16} /> {project.event_date || "Date TBD"}
          </div>
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin size={16} /> <span className="line-clamp-1">{getLocation()}</span>
          </div>
        </div>

        <div className="text-sm flex items-center gap-2 text-muted-foreground">
          <Clock size={16} /> {project.duration_hours ? `${project.duration_hours} Hours` : "Duration TBD"}
        </div>
      </div>

      <hr className="" />

      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-300">
        <div className="px-6 py-4">
          <p className="text-xs font-medium mb-2 text-muted-foreground uppercase tracking-wider">Streaming Platforms</p>
          <div className="flex flex-wrap gap-2 max-h-[90px] overflow-y-auto pr-1">
            {platforms.length > 0 ? platforms.map((platformId, index) => (
              <span key={index} className="border px-3 py-1 rounded-md text-xs bg-popover flex items-center gap-2 whitespace-nowrap">
                <Video size={14} /> {platformNames[platformId] || platformId || "Unknown"}
              </span>
            )) : (
              <span className="text-xs text-muted-foreground">None specified</span>
            )}
          </div>
        </div>
        <div className="px-6 py-4">
          <p className="text-xs font-medium mb-2 text-muted-foreground uppercase tracking-wider">Equipment Assigned</p>
          <div className="flex flex-wrap gap-2 max-h-[90px] overflow-y-auto pr-1">
            {equipment.length > 0 ? equipment.map((item, index) => (
              <span key={index} className="border px-3 py-1 rounded-md text-xs bg-popover flex items-center gap-2 whitespace-nowrap">
                <Mic size={14} /> {item}
              </span>
            )) : (
              <span className="text-xs text-muted-foreground">None assigned</span>
            )}
          </div>
        </div>
      </div>

      <hr className="" />

      <div className="flex justify-between items-center p-6 text-sm ">
        <p className="text-[#8C8C8C]">
          {project.created_at ? `Created ${new Date(project.created_at).toLocaleDateString()}` : ""}
        </p>
        <button
          className="bg-button-coloured hover:bg-button-coloured/80 text-button-coloured-foreground px-5 py-2 rounded-md ml-auto font-medium flex items-center gap-2 disabled:opacity-50"
          onClick={handleManageClick}
          disabled={loading}
        >
          {loading ? (
            <><Loader2 size={16} className="animate-spin" /> Initializing...</>
          ) : (
            "Manage LiveStream"
          )}
        </button>
      </div>
    </div>
  )
}

export default LiveStreamCard;
